import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { Link } from 'react-router-dom'
import { BaseSelect } from 'src/Libs/inputs/react-hook-form/Select/BaseSelect'
import { SettingInfoBox, SettingsBox } from 'src/Elements/Data'
import { useLatestState } from 'src/Libs/flow/advanced-settings/hooks'
import { syncInstallation } from 'src/Libs/flow/installation/api'
import { ModbusTcpChargingStation } from 'src/Libs/flow/installation/forms'
import { installationDevicesKey } from 'src/Libs/flow/installation/hooks'
import { getModbusTcpPUTDto } from 'src/Libs/flow/parsers'
import { SETTINGS_ROUTES } from '../../general/constants'
import { ControlledModbusTcpPUTDto, updateEvse } from '../api/evse.api.js'
import { infoBoxContentStyle } from '../constants'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useAppRatingContext } from 'src/Libs/app-rating/hooks'
import { usePortalMutation } from 'src/Libs/api'

const ACCESS_CONTROL_OPTIONS = [
  {
    value: 'freeAccess',
    labelKey: 'user-settings.evse-access-page.options.anyone',
    descriptionKey: 'user-settings.evse-access-page.options.anyone-description',
  },
  {
    value: 'badge',
    labelKey: 'user-settings.evse-access-page.options.badges',
    descriptionKey: 'user-settings.evse-access-page.options.badges-description',
  },
  {
    value: 'key',
    labelKey: 'user-settings.evse-access-page.options.key',
    descriptionKey: 'user-settings.evse-access-page.options.key-description',
  },
]

interface AccessControlProps {
  installationId: number
  storageId?: string
  queryIsLoading: boolean
  evse: ModbusTcpChargingStation
}

/** The access control page of an installation's (user-level) charging station settings. */
export const AccessControl: React.FC<AccessControlProps> = ({ installationId, queryIsLoading, evse, storageId }) => {
  const { t } = useCustomTranslation()
  const { triggerAppRating } = useAppRatingContext()

  const { enqueueErrorStack } = useSnackbar()

  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()

  const currentAuthenticationMode = evse.accessManagementOptions
  const carAtHomeAuthEnabled = evse.carAtHomeBehavior.isEnabled
  const [authenticationMode, setAuthenticationMode] = useLatestState(currentAuthenticationMode)

  const authFetch = useAuthFetchWithResult()
  const queryClient = useQueryClient()
  const mutateEvse = usePortalMutation({
    mutationFn: (newEvse: ControlledModbusTcpPUTDto) =>
      updateEvse(installationId, evse.rawData?.deviceLinkId?.toString() ?? '', evse.rawData?.id?.toString() ?? '', newEvse, authFetch).then(() =>
        syncInstallation(installationId, authFetch)
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: installationDevicesKey(installationId),
      }),
    onError: () => {
      enqueueErrorStack(t('common.update-failed-error'))
      setAuthenticationMode(currentAuthenticationMode)
    },
  })

  const updateAuthenticationMode = (newAuthenticationMode: ModbusTcpChargingStation['accessManagementOptions']): void => {
    if (authenticationMode === newAuthenticationMode) return

    setAuthenticationMode(newAuthenticationMode)
    mutateEvse.mutate(
      getModbusTcpPUTDto({
        ...evse,
        accessManagementOptions: newAuthenticationMode,
      })
    )
  }

  const isKeyOptionVisible = evse?.reference !== 'XVL122C / XVL122S'
  const availableOptions = ACCESS_CONTROL_OPTIONS.filter(opt => opt.value !== 'key' || isKeyOptionVisible)

  const isLoading = queryIsLoading || mutateEvse.isPending
  const isDisabled = isLoading || !userCanEdit
  const selectedConfig = availableOptions.find(opt => opt.value === authenticationMode) ?? availableOptions[0]

  return (
    <AccordionItem title={t('user-settings.evse-page.access-control')} expanded accordionDetailsSx={{ px: 4, py: 2 }}>
      <Stack>
        <SettingsBox
          title={t('user-settings.evse-access-page.options.title')}
          suffix={
            <Box sx={{ minWidth: { xs: '100%', sm: '50%' } }}>
              <BaseSelect
                value={authenticationMode}
                onChange={e => {
                  updateAuthenticationMode(e.target.value as ModbusTcpChargingStation['accessManagementOptions'])
                  triggerAppRating()
                }}
                disabled={isDisabled}
                fullWidth
                options={availableOptions.map(opt => ({
                  value: opt.value,
                  label: t(opt.labelKey),
                  disabled: opt.value === 'freeAccess' && carAtHomeAuthEnabled && authenticationMode === 'freeAccess',
                }))}
              />
            </Box>
          }
        />
        <SettingInfoBox title={t(selectedConfig.labelKey)} description={t(selectedConfig.descriptionKey)}>
          <InfoBoxContent authenticationMode={authenticationMode} installationId={installationId} storageId={storageId} isLoading={isLoading} />
        </SettingInfoBox>
      </Stack>
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress />
        </Box>
      )}
    </AccordionItem>
  )
}

// #region InfoBoxContent
interface InfoBoxContentProps {
  authenticationMode: string
  installationId: number
  storageId?: string
  isLoading: boolean
}

const InfoBoxContent: React.FC<InfoBoxContentProps> = ({ authenticationMode, installationId, storageId, isLoading }) => {
  const { t } = useCustomTranslation()

  if (isLoading) return null

  if (authenticationMode === 'badge') {
    return (
      <Box sx={infoBoxContentStyle}>
        <Typography variant="body1" sx={{ color: 'grey.800' }}>
          {t('user-settings.evse-access-page.options.badges-prompt')}
        </Typography>
        <Button
          variant="contained"
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
          component={Link}
          to={`/installation/${installationId}/${storageId}/properties/${SETTINGS_ROUTES.badges}`}
        >
          {t('user-settings.evse-access-page.options.badges-button')}
        </Button>
      </Box>
    )
  }

  if (authenticationMode === 'key') {
    return (
      <Box sx={infoBoxContentStyle}>
        <Typography variant="body1" sx={{ color: 'grey.800' }}>
          {t('user-settings.evse-access-page.options.key-information')}
        </Typography>
      </Box>
    )
  }

  return null
}
